import { CHALLENGE_TEXT, challengeFor } from '../content/challenges';
import type { LevelDef } from '../content/levelSchema';
import type { MowerId } from '../content/mowers';
import { goldGoal, parGoal } from '../meta/economy';
import { STAR } from '../meta/save/schema';
import { TICK_RATE } from '../sim/constants';
import { formatTime } from './ui/time';

export interface StarGoals {
  /** What clears the lawn (it marks the lawn done). */
  objective: string;
  /** The three stars in display order: par (or the boss's own goal), all coins, the challenge. */
  goals: [bit: number, text: string][];
}

/**
 * The time that wins the par star with this mower (ticks) at the Tuned Engine level in use, or null
 * if the lawn has none: the par route, its zone's slack, the engine (economy.ts, `parGoal`).
 */
export function parGoalTicks(
  level: LevelDef,
  mower: MowerId | undefined,
  engine = 0,
): number | null {
  const par = mower ? level.par[mower] : undefined;
  return par ? parGoal(par, level.level, engine) : null;
}

/** The gold time with this mower (ticks) at the engine in use, or null where there is none. */
export function goldGoalTicks(
  level: LevelDef,
  mower: MowerId | undefined,
  engine = 0,
): number | null {
  const gold = mower ? level.goldTime?.[mower] : undefined;
  return gold ? goldGoal(gold, engine) : null;
}

/**
 * A lawn's objective and star goals, as the level card shows them before mowing and the results
 * show them after (owner, 2026-10-21: a missed middle star needs its goal beside it). The par goal
 * depends on the mower.
 */
export function starGoals(level: LevelDef, mower: MowerId | undefined, engine = 0): StarGoals {
  const coins = (level.sim.pickups ?? []).filter((p) => p.kind === 'coin').length;
  const par = parGoalTicks(level, mower, engine);
  const storm = level.sim.storm;
  const art = level.sim.art;
  const coinGoal: [number, string] = [
    STAR.allCoins,
    coins > 0 ? `Grab all ${coins} coins` : 'No coins here',
  ];
  const parGoal: [number, string] = [
    STAR.par,
    par ? `Beat ${formatTime(par, TICK_RATE)}` : 'Be quick',
  ];
  const [objective, goals]: [string, [number, string][]] = art
    ? [
        art.goal ? "Mow it all, then the judges' table" : 'Mow the picture in its stripes',
        [
          [STAR.par, `Match ${Math.round(art.stars[0] * 100)}% of the picture`],
          [STAR.allCoins, `Match ${Math.round(art.stars[1] * 100)}%: best in show`],
        ],
      ]
    : level.sim.soccer
      ? [
          // Mower soccer: more goals and a tidy pitch replace time and coins (starsFor).
          `Score ${level.sim.soccer.target} goals by halftime`,
          [
            [STAR.par, `Score ${level.sim.soccer.stars.goals} goals`],
            [STAR.allCoins, `Mow ${Math.round(level.sim.soccer.stars.mowed * 100)}% of the pitch`],
          ],
        ]
      : level.sim.rival
        ? [
            'Mow more than the robot',
            [[STAR.par, `Mow ${Math.round(level.sim.rival.target * 100)}% of the lawn`], coinGoal],
          ]
        : level.sim.herd
          ? ['Pen every sheep, mow it all', [parGoal, coinGoal]]
          : storm
            ? [
                'Beat the rain to the shed',
                [[STAR.par, `Mow ${Math.round(storm.target * 100)}% on the way`], coinGoal],
              ]
            : [
                level.sim.dark
                  ? 'Mow every blade in the dark'
                  : level.sim.party
                    ? 'Mow the maze before the party'
                    : 'Mow every blade',
                [parGoal, coinGoal],
              ];
  const challenge = challengeFor(level);
  const gold = challenge === 'gold' ? goldGoalTicks(level, mower, engine) : null;
  goals.push([
    STAR.challenge,
    gold ? `Gold time: ${formatTime(gold, TICK_RATE)}` : CHALLENGE_TEXT[challenge],
  ]);
  return { objective, goals };
}
